import { useState } from 'react';
import { ScenarioBar } from './AnalysisVisuals';

/**
 * BullBearCalculator — Interactive scenario calculator
 *
 * Shows what an investment would become in 3 scenarios (bull/real/bear)
 * by a target horizon year, based on stock-specific multipliers.
 *
 * Props:
 *  - symbol: stock ticker (for display)
 *  - scenarios: { horizon, bull, real, bear } from analysisBank
 */
export default function BullBearCalculator({ symbol, scenarios }) {
  const [amount, setAmount] = useState(1000);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  if (!scenarios || !scenarios.bull) return null;

  const presets = [100, 500, 1000, 5000, 10000];

  const calculate = (multiplier) => Math.round(amount * multiplier);
  const calcReturn = (multiplier) => ((multiplier - 1) * 100).toFixed(0);

  const yearsAway = scenarios.horizon - new Date().getFullYear();

  const cases = [
    {
      key: 'bull',
      label: 'Optimista',
      emoji: '🚀',
      color: '#059669',
      bg: '#ECFDF5',
      border: '#A7F3D0',
      data: scenarios.bull,
    },
    {
      key: 'real',
      label: 'Realista',
      emoji: '⚖️',
      color: '#2A59C6',
      bg: '#F0F4FF',
      border: '#C7D2E8',
      data: scenarios.real,
    },
    {
      key: 'bear',
      label: 'Pesimista',
      emoji: '⚠️',
      color: '#DC2626',
      bg: '#FEF2F2',
      border: '#FECACA',
      data: scenarios.bear,
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🔮</span>
        <div>
          <div className="text-sm font-semibold text-[#181D27]">Calculadora de Escenarios</div>
          <div className="text-[11px] text-[#9CA3AF]">
            ¿Qué pasaría con tu inversión en {symbol} para {scenarios.horizon}? ({yearsAway} años)
          </div>
        </div>
      </div>

      {/* Amount selector */}
      <div className="mb-4">
        <div className="text-xs font-semibold text-[#6B7280] mb-2">Si inviertes hoy:</div>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => { setAmount(p); setShowCustom(false); setCustomInput(''); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                amount === p && !showCustom
                  ? 'bg-[#2A59C6] text-white border-[#2A59C6]'
                  : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#2A59C6]'
              }`}
            >
              ${p.toLocaleString()}
            </button>
          ))}
          <button
            onClick={() => setShowCustom(!showCustom)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
              showCustom
                ? 'bg-[#2A59C6] text-white border-[#2A59C6]'
                : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#2A59C6]'
            }`}
          >
            Otro
          </button>
        </div>
        {showCustom && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-[#6B7280]">$</span>
            <input
              type="number"
              value={customInput}
              onChange={(e) => {
                const v = e.target.value;
                setCustomInput(v);
                const n = parseFloat(v);
                if (!isNaN(n) && n > 0) setAmount(n);
              }}
              placeholder="Tu monto"
              className="flex-1 max-w-[140px] px-3 py-1.5 border border-[#E5E7EB] rounded-lg text-sm outline-none focus:border-[#2A59C6]"
            />
          </div>
        )}
      </div>

      {/* Visual bar chart */}
      <ScenarioBar scenarios={scenarios} amount={amount} />

      {/* Scenario cards */}
      <div className="space-y-2.5">
        {cases.map((c) => {
          const finalValue = calculate(c.data.multiplier);
          const returnPct = calcReturn(c.data.multiplier);
          const isPositive = c.data.multiplier >= 1;
          return (
            <div
              key={c.key}
              className="rounded-xl p-3 border"
              style={{ backgroundColor: c.bg, borderColor: c.border }}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-base">{c.emoji}</span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: c.color }}>
                      Escenario {c.label}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">{c.data.label}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold" style={{ color: c.color }}>
                    ${finalValue.toLocaleString()}
                  </div>
                  <div className="text-[10px] font-semibold" style={{ color: c.color }}>
                    {isPositive ? '+' : ''}{returnPct}% ({c.data.multiplier}x)
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-[#4B5563] leading-relaxed">
                <strong>¿Por qué?</strong> {c.data.why}
              </p>
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="mt-3 text-[10px] text-[#9CA3AF] leading-relaxed">
        Estos escenarios son proyecciones basadas en análisis del Oráculo. No son garantías. Los rendimientos pasados no predicen resultados futuros. Esta calculadora es solo educativa.
      </div>
    </div>
  );
}
